import { useCallback, useEffect, useRef, type FunctionComponent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { IconButton } from '@/core/tamagoshiTailwind/components/IconButton/IconButton';
import { CrossIcon } from '@/core/tamagoshiTailwind/icons/CrossIcon';
import { twMerge } from '@/core/tamagoshiTailwind/util/twMerge';
import { setMenuVisibility } from '@/domains/productDiscovery/HeaderLeonidas/HeaderLeonidas.actions';
import { useLazyHeader } from '@/domains/productDiscovery/HeaderLeonidas/hooks/useLazyHeader';
import {
  useSelectedCategory,
  useSelectedFamily,
} from '@/domains/productDiscovery/HeaderLeonidas/hooks/useMenuVisibility';
import { useOnClickOutside } from '@/domains/productDiscovery/HeaderLeonidas/hooks/useOnClickOutside';
import { useGetOverlay } from '@/domains/productDiscovery/HeaderLeonidas/hooks/useOverlay';
import { OVERLAY_MENU } from '@/domains/productDiscovery/HeaderLeonidas/interfaces/HeaderLeonidas.model';
import { selectMenuCategories } from '@/domains/productDiscovery/HeaderLeonidas/selectors/selectors';
import { isMobile } from '@/domains/productDiscovery/HeaderLeonidas/utils/isMobile';
import type { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import { MenuCategories } from '@/productDiscovery/HeaderLeonidas/components/Menu/components/MenuCategories';
import { MenuFamilies } from '@/productDiscovery/HeaderLeonidas/components/Menu/components/MenuFamilies';
import { MenuSubCategories } from '@/productDiscovery/HeaderLeonidas/components/Menu/components/MenuSubCategories';
import { leonidasMenuCloseText } from '@/productDiscovery/HeaderLeonidas/components/MenuCloseButton/translations';
import type { MenuCategory } from '@/productDiscovery/HeaderLeonidas/interfaces/MenuTree.model';

export const MenuInteractive: FunctionComponent = () => {
  const dispatch = useDispatch();
  const selectedFamily = useSelectedFamily();
  const selectedCategory = useSelectedCategory();
  const menuRef = useRef(null);
  const getOverlay = useGetOverlay();

  const categories = useSelector<
    SpartacuxProductDiscoveryDomainState,
    MenuCategory[] | undefined
  >(selectMenuCategories);

  useLazyHeader(true);

  const hideMenu = useCallback(() => {
    if (getOverlay() === OVERLAY_MENU && !isMobile()) {
      dispatch(setMenuVisibility(false));
    }
  }, [dispatch, getOverlay]);

  useOnClickOutside(menuRef, hideMenu);

  /**
   * Close menu on ESC
   */
  useEffect(() => {
    if (window) {
      const listen = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          dispatch(setMenuVisibility(false));
        }
      };
      window.addEventListener('keyup', listen);

      return () => window.removeEventListener('keyup', listen);
    }
  }, [dispatch]);

  return (
    <div
      className={twMerge(
        `fixed bottom-0 left-0 top-0 z-[11] w-full bg-foundation-inverted
        desktop:absolute desktop:bottom-[unset] desktop:right-[unset]
        desktop:top-[100%] desktop:h-[calc(100vh-140px)] desktop:w-[360px]`,
        selectedCategory && 'desktop:w-[884px]',
      )}
      ref={menuRef}
    >
      <nav className="relative h-full w-full">
        {!selectedFamily && <MenuFamilies families={categories} />}
        {selectedFamily && (
          <>
            <MenuCategories selectedFamily={selectedFamily} />
            {selectedCategory && (
              <MenuSubCategories
                selectedFamily={selectedFamily}
                selectedCategory={selectedCategory}
              />
            )}
          </>
        )}

        <div className={twMerge('absolute right-[13px] top-[17px] z-[13]')}>
          <IconButton
            aria-label={leonidasMenuCloseText}
            icon={CrossIcon}
            onClick={() => dispatch(setMenuVisibility(false))}
            size="M"
          />
        </div>
      </nav>
    </div>
  );
};

MenuInteractive.displayName = 'MenuInteractive';
